/**
 * MapSurface — the single map surface behind `/map`.
 *
 * The left slot holds one card at a time: a zone (`?zone=`), a deal (`?deal=`),
 * a location (`?location=`) or a place. `useMapSelection` owns the three URL
 * params and keeps them exclusive. The surface mounts a SINGLE persistent
 * `MapShell`; the cards and their side effects mount over it, so opening or
 * closing one never tears the map down. {@link MapZoneFocus} outlines the
 * selected zone and fits the camera to it only on an explicit focus intent.
 * Otherwise the camera opens at the shell's default view and only ever moves on
 * a user action.
 */
import { useQueryClient } from '@tanstack/react-query';
import mapboxgl, { type LngLatLike } from 'mapbox-gl';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import { toast } from 'sonner';
import { notifyError } from '@/libs/bugsnag';
import { ConfirmDeleteModal } from '@/components/molecules/ConfirmDeleteModal';
import { FEATURE_FLAGS } from '@/features/api/constants';
import { useFeatureFlag } from '@/features/api/hooks/useFeatureFlag';
import type { DealOpenSource } from '@/features/deals/utils/mixpanelEvents';
import { DealMapCard } from '@/features/locations/components/DealMapCard';
import type { DealsStagePanelState } from '@/features/locations/components/DealsStageSync';
import { DealsStageSync } from '@/features/locations/components/DealsStageSync';
import type { MapShellRenderState } from '@/features/locations/components/MapShell';
import { MapShell } from '@/features/locations/components/MapShell';
import { useLayerVisibilityFilters } from '@/features/locations/hooks/useLayerVisibilityFilters';
import { usePlaceCursorLink } from '@/features/locations/hooks/usePlaceLink';
import type { SelectedZone } from '@/features/locations/hooks/useZoneInteractions';
import type { OwnerSelection } from '@/features/locations/utils/MapLayersService';
import { ZONE_TYPES, type ZoneType } from '@/features/locations/utils/zoneColors';
import type { PointOfInterestPlace } from '@/features/locations/utils/pointOfInterest';
import {
  type ComputeRun,
  type Coordinates,
  ManualPinProvider,
  useManualPin,
} from '@/features/map/context/ManualPinContext';
import { useLaunchLocationCompute } from '@/features/map/hooks/useLaunchLocationCompute';
import { useMapSelection } from '@/features/map/hooks/useMapSelection';
import {
  rollbackOptimisticMarkers,
  useWorkspaceCompute,
} from '@/features/map/hooks/useWorkspaceCompute';
import { zoneIdAtLngLat } from '@/features/map/utils/zoneIdAtLngLat';
import type { AddressLines } from '@/features/mapbox/utils/featureAddressLines';
import { useCancelProject } from '@/features/projects/services/project.api';
import { WorkspaceFormModal } from '@/features/workspaces/components/WorkspaceFormModal';
import { useActiveWorkspace } from '@/features/workspaces/hooks/useActiveWorkspace';
import {
  useAllWorkspacesQuery,
  useDeleteWorkspace,
  useWorkspaceLocationMarkersQuery,
  useWorkspaceQuery,
} from '@/features/workspaces/services/workspace.api';
import { locationLabel } from '@/features/workspaces/utils/locationLabel';
import { ZoneMapCard } from '@/features/zones/components/ZoneMapCard';
import { useZoneByIdQuery, zonesKeyFactory } from '@/features/zones/services/zones.api';
import { getErrorStatus, isUnexpectedZonesError } from '@/features/zones/utils/mapZonesApiError';
import { useMixpanel } from '@/hooks/useMixpanel';
import { ACTION_TRACKED } from '@/libs/mixpanel';
import { parseBackendError } from '@/libs/utils/parseBackendErrors';
import type { IWorkspace } from '@/types/api';
import { MapLayerFilters } from './MapLayerFilters';
import { MapOwnerFilter } from './MapOwnerFilter';
import { MapSearchBar } from './MapSearchBar';
import { MapToolbox } from './MapToolbox';
import { MapTopBar } from './MapTopBar';
import { MapWorkspaceEmptyLocations } from './MapWorkspaceEmptyLocations';
import { MapWorkspaceLayers } from './MapWorkspaceLayers';
import { MapWorkspaceOnboarding } from './MapWorkspaceOnboarding';
import { MapWorkspaceSelectHint } from './MapWorkspaceSelectHint';
import { MapZoneFocus } from './MapZoneFocus';
import { NavbarWorkspaceSwitcher } from './NavbarWorkspaceSwitcher';
import { PromotedLocationRedirect } from './PromotedLocationRedirect';
import { ADD_PIN_TOOL_ID } from './tools/mapToolRegistry';

// ── Constants ────────────────────────────────────────────────────────────────

/** Stable identity key for the single map instance. */
const MAP_SURFACE_ID = 'map-surface';

/** Default view (Europe) used before any zone geometry is available. */
const MAP_DEFAULT_CENTER: LngLatLike = [10, 50];
const MAP_DEFAULT_ZOOM = 4;

const ZONE_NOT_FOUND_TOAST =
  'This zone was not found. It may have been removed, or the link is out of date.';
const ZONE_UNREACHABLE_TOAST = "Couldn't reach this zone. Try opening the link again in a moment.";

// ── MapSurface — public component ──────────────────────────────────────────

/**
 * Public entry point. Mounts the {@link ManualPinProvider} around the surface
 * body so the armed-tool + compute-run state is shared by the surface and the
 * toolbox panel. The provider cannot live in the same component that calls
 * `useManualPin`, so the body is split out into {@link MapSurfaceInner}.
 */
export function MapSurface() {
  return (
    <ManualPinProvider>
      <MapSurfaceInner />
    </ManualPinProvider>
  );
}

// ── MapSurfaceInner — surface body (consumes useManualPin) ───────────────────

function MapSurfaceInner() {
  const { state: locationState } = useLocation();
  const trackAction = useMixpanel((s) => s.trackAction);

  const {
    zoneId,
    selectZone,
    clearZone,
    locationId,
    selectLocation,
    clearLocation,
    dealId,
    selectDeal,
    clearDeal,
  } = useMapSelection();

  // What opened the card, which only the surface can tell apart: a click on the map, the creation
  // that landed the deal, or neither — a restored or deep-linked `?deal=`, which is where a card
  // present on the first render came from.
  const [dealOpenSource, setDealOpenSource] = useState<DealOpenSource>('deep_link');

  // The corner slot shows one card: the hook's setters drop the other selection.
  const handleDealSelect = useCallback(
    (id: string | null) => {
      if (id === null) clearDeal();
      else {
        setDealOpenSource('map_click');
        selectDeal(id);
      }
    },
    [clearDeal, selectDeal],
  );

  // A promotion is celebrated on the map, an ordinary deal click is not, so the id a creation
  // landed is held until its card has played the burst.
  const [justCreatedDealId, setJustCreatedDealId] = useState<string | null>(null);
  const handleDealCreated = useCallback(
    (id: string) => {
      setJustCreatedDealId(id);
      setDealOpenSource('just_created');
      selectDeal(id);
    },
    [selectDeal],
  );
  const hasZone = zoneId != null;

  /** Whether a card holds the left slot. The place card is the shell's, so it reports it. */
  function slotCardOpen(shell: MapShellRenderState): boolean {
    return (
      (hasZone && zone != null) || dealId != null || locationId != null || shell.placeOpen === true
    );
  }

  // ── Workspace management slot ────────────────────────────────────────────────
  // The active workspace lives in the `?workspace=<id>` URL param; selecting or
  // creating one only updates that param.
  const { activeId, setActive, clearActive } = useActiveWorkspace();
  const [formState, setFormState] = useState<
    { mode: 'create' } | { mode: 'edit'; workspace: IWorkspace } | null
  >(null);
  const [deleteTarget, setDeleteTarget] = useState<IWorkspace | null>(null);
  const deleteWorkspace = useDeleteWorkspace();

  // ── Manual-pin: armed-tool state comes from the provider so the
  //    toolbox and the map-click handler agree. The controlled MapToolbox
  //    arms/disarms via arm()/disarm(). ──────────────────────────────────────
  // The compute run is registered by useLaunchLocationCompute (on launch
  // success); the surface only reads the active runs and clears them on their
  // SSE terminal.
  const { armedToolId, arm, disarm, activeComputeRuns, pendingTempIds, clearComputeRun } =
    useManualPin();
  // The place a drop was started from. The next click sends it with the location,
  // and the dotted line follows the pointer until the tool is put away.
  const [dropPlace, setDropPlace] = useState<PointOfInterestPlace | null>(null);
  useEffect(() => {
    if (armedToolId !== ADD_PIN_TOOL_ID) setDropPlace(null);
  }, [armedToolId]);
  // Shared by MapSearchBar's inline Add-location trigger and MapToolbox's
  // Driving time tool — both are controlled off the same armedToolId slot.
  const handleArmChange = useCallback(
    (id: string | null) => (id ? arm(id) : disarm()),
    [arm, disarm],
  );
  // Opens the location panel on launch (skeleton state), not on settle.
  const launchLocationCompute = useLaunchLocationCompute(selectLocation);

  // Single-pin auto-select request raised on a compute COMPLETED. The
  // surface owns it and hands it to MapWorkspaceLayers, which opens the
  // reconciled marker's detail.
  const [autoSelectId, setAutoSelectId] = useState<string | null>(null);

  // Location selection is workspace-scoped view-state: whenever the active
  // workspace changes — created, switched via the switcher, or cleared by
  // deleting the active one — a selection left over from the previous
  // context must not leak its GeoJSON into the new one.
  // `MapWorkspaceLayers` already resets on a `workspaceId` prop change, but
  // it unmounts entirely while there is no active workspace, so that reset
  // never fires for a null -> <id> transition (fresh create, or selecting a
  // workspace right after deleting the active one). Owning the reset here —
  // MapSurfaceInner never unmounts — covers every transition uniformly.
  // `autoSelectId` must be cleared alongside it: MapWorkspaceLayers resets
  // its own consumed-auto-select guard on the same workspaceId change (so a
  // re-add on a re-activated workspace can auto-select again), which would
  // otherwise treat a stale `autoSelectId` left over from the previous
  // workspace as a fresh request and immediately re-select it.
  const lastActiveWorkspaceIdRef = useRef(activeId);
  useEffect(() => {
    if (lastActiveWorkspaceIdRef.current === activeId) return;
    lastActiveWorkspaceIdRef.current = activeId;
    clearLocation();
    setAutoSelectId(null);
  }, [activeId, clearLocation]);

  // Latest-value ref for the currently-selected location — read by
  // useWorkspaceCompute at completion time (which can be long after this
  // render) to skip auto-select if the user has since selected something
  // else. A plain value would be stale by then; this ref never is.
  const selectedLocationIdRef = useRef<string | null>(null);
  selectedLocationIdRef.current = locationId;

  // Live 0–100 percent from the active run's SSE stream, shown in the
  // location panel's skeleton. Cleared alongside the run on settle.
  const [computeProgress, setComputeProgress] = useState<number | null>(null);

  // Blocks the search bar and add-pin tool while a run is in flight — starting
  // the instant a launch fires (pendingTempIds), not only once the add
  // mutation resolves into a registered ComputeRun.
  const isComputing = activeComputeRuns.length > 0 || pendingTempIds.length > 0;

  // tempIds still computing; MapWorkspaceLayers uses this to defer the results
  // fetch. Includes pendingTempIds so a just-launched tempId — not yet a real
  // backend location — never gets fetched and 404s.
  const computingLocationIds = useMemo(
    () => new Set([...pendingTempIds, ...activeComputeRuns.flatMap((run) => run.tempIds)]),
    [activeComputeRuns, pendingTempIds],
  );

  // The run behind the currently-selected (computing) location, if any — its
  // projectId is what the popup's Cancel button targets. `selectLocation`
  // writes the tempId as `locationId` the instant a run launches (see
  // ManualPinContext), so matching on `run.tempIds` finds it before the run
  // has a real backend location id.
  const computingRun = useMemo(
    () => activeComputeRuns.find((run) => locationId != null && run.tempIds.includes(locationId)),
    [activeComputeRuns, locationId],
  );

  // The active workspace's own run, if any — scopes the pin loader below to
  // the workspace it belongs to. Runs launched in a different workspace keep
  // following via MapComputeRunFollower (so they still complete/toast) but
  // their marker must not render over a workspace they don't belong to.
  const activeWorkspaceComputeRun = useMemo(
    () => activeComputeRuns.find((run) => run.workspaceId === activeId),
    [activeComputeRuns, activeId],
  );

  // Cancel the selected location's active compute run. The backend SSE
  // stream has no distinct terminal event for `cancelled` (see
  // useWorkspaceCompute), so none of the usual FAILED-handling runs — this
  // does it explicitly: cancel the project, undo the optimistic marker, drop
  // the run, and close the popup.
  const queryClient = useQueryClient();
  const cancelProject = useCancelProject();
  const handleCancelComputing = useCallback(
    (projectId: string) => {
      const run = activeComputeRuns.find((r) => r.projectId === projectId);
      if (!run) return;
      cancelProject.mutate(
        { projectId },
        {
          onSuccess: () => {
            rollbackOptimisticMarkers(queryClient, run.workspaceId, run.tempIds);
            clearComputeRun(projectId);
            setComputeProgress(null);
            clearLocation();
          },
        },
      );
    },
    [activeComputeRuns, cancelProject, queryClient, clearComputeRun, clearLocation],
  );

  // ── Resilient active workspace context ───────────────────────────────────────
  // Mirror the query already fired by the switcher — TanStack dedupes it to a
  // single fetch.  When the active id resolves to a 404/403 (stale link, deleted
  // workspace, change of owner) we show a friendly message and fall back to the
  // no-workspace state so the map stays usable.
  const activeWorkspace = useWorkspaceQuery(activeId);

  // A confirmed empty list — fetched, zero rows — never while loading or errored.
  const allWorkspaces = useAllWorkspacesQuery();
  const isOnboarding = allWorkspaces.data != null && allWorkspaces.data.workspaces.length === 0;

  // Workspaces exist but none selected: hint at the switcher and block the
  // search bar (onboarding is reserved for a truly empty account).
  const needsWorkspaceSelection =
    activeId == null && allWorkspaces.data != null && allWorkspaces.data.workspaces.length > 0;

  // Active workspace confirmed to have zero locations: hint at the search bar
  // and toolbox instead of hiding them (hiding them is onboarding's job when
  // there's no workspace at all). Keyed off the markers query rather than
  // `location_count` because `useAddWorkspaceLocations` appends an optimistic
  // marker on launch, so this clears the instant a pin is dropped — waiting on
  // `location_count` would leave it visible until the SSE terminal refetches.
  const workspaceLocationMarkers = useWorkspaceLocationMarkersQuery(activeId);
  const isWorkspaceEmpty =
    activeId != null &&
    workspaceLocationMarkers.data != null &&
    workspaceLocationMarkers.data.markers.length === 0;

  // Track the last id we have already handled so the effect fires only once per
  // bad id.  After clearActive() the id becomes null and the query disables;
  // without this guard a re-render could re-fire the toast before the param
  // fully clears.  The guard is reset as soon as the param clears (below) so a
  // re-selected id is never permanently muted for the session.
  const handledStaleIdRef = useRef<string | null>(null);

  useEffect(() => {
    // Param cleared (clearActive ran, or the user navigated away): drop the
    // guard so the SAME id can toast again if it is later re-selected and is
    // still — or once more — inaccessible (e.g. a workspace that was deleted,
    // then recreated/re-shared under the same id, then revoked again).
    if (!activeId) {
      handledStaleIdRef.current = null;
      return;
    }
    if (!activeWorkspace.isError) return;

    const httpCode =
      activeWorkspace.error instanceof Error
        ? parseBackendError(activeWorkspace.error.message).httpCode
        : 0;

    if (httpCode !== 403 && httpCode !== 404) return;
    if (handledStaleIdRef.current === activeId) return;

    handledStaleIdRef.current = activeId;
    toast.error('This workspace could not be found or no longer belongs to you.');
    clearActive();
  }, [activeId, activeWorkspace.isError, activeWorkspace.error, clearActive]);

  // Search → launch: a successful search result immediately launches its own
  // compute run at the result coordinates — no staging, no arming the add-pin
  // tool. Stable identity (launchLocationCompute is useCallback-stable) so the
  // prop never churns MapSearchBar.
  const handleSearchResult = useCallback(
    (
      coords: { latitude: number; longitude: number },
      zoneId: string | null,
      addressLines?: AddressLines | null,
    ) => {
      launchLocationCompute(coords, { source: 'search', zoneId, addressLines });
    },
    [launchLocationCompute],
  );

  // Map click while add-pin is armed → launch, then disarm: one click places
  // one pin and turns the tool off; another pin needs re-arming.
  const handlePinClick = useCallback(
    (coords: { latitude: number; longitude: number }, zoneId: string | null) => {
      launchLocationCompute(coords, {
        source: 'drop_pin',
        zoneId,
        place: dropPlace ?? undefined,
      });
      disarm();
    },
    [launchLocationCompute, disarm, dropPlace],
  );

  const mapTilesEnabled = useFeatureFlag(FEATURE_FLAGS.map_tiles_enabled);

  // Single source of overlay state across both modes — owned here so the toggle
  // survives mode switches (the persistent shell never remounts).
  const [zonesOverlayVisible, setZonesOverlayVisible] = useState(true);
  const [visibleZoneTypes, setVisibleZoneTypes] = useState<readonly ZoneType[]>(ZONE_TYPES);
  const toggleZoneType = useCallback((type: ZoneType) => {
    setVisibleZoneTypes((current) =>
      current.includes(type) ? current.filter((value) => value !== type) : [...current, type],
    );
  }, []);
  const toggleZonesOverlay = useCallback(
    (visible: boolean) => {
      setZonesOverlayVisible(visible);
      if (visible) setVisibleZoneTypes((current) => (current.length === 0 ? ZONE_TYPES : current));
      trackAction(ACTION_TRACKED.MAP_LAYER_TOGGLED, { layer: 'zones', scope: 'group', visible });
    },
    [trackAction],
  );

  // Owned here so it survives zone↔map navigation. Empty means every owner.
  const [ownerSelection, setOwnerSelection] = useState<OwnerSelection>([]);
  const toggleOwner = useCallback(
    (owner: string) => {
      const next = ownerSelection.includes(owner)
        ? ownerSelection.filter((o) => o !== owner)
        : [...ownerSelection, owner];
      trackAction(ACTION_TRACKED.MAP_OWNER_FILTERED, { owner, selected: next.length });
      setOwnerSelection(next);
    },
    [ownerSelection, trackAction],
  );
  const clearOwners = useCallback(() => {
    trackAction(ACTION_TRACKED.MAP_OWNER_FILTERED, { owner: null, selected: 0 });
    setOwnerSelection([]);
  }, [trackAction]);

  // ── Mirrored HubSpot deals: per-stage toggle ──────────────────────────────────
  // Lifted from DealsStageSync so MapLeftControls can render it.
  const [dealsPanel, setDealsPanel] = useState<DealsStagePanelState>({
    stages: [],
    toggleStage: () => {},
    toggleAllStages: () => {},
  });

  // ── Layer-visibility filters ─────────────────────────────────────────────────
  // Owned at the surface level (like zonesOverlayVisible above) so the filter
  // selection survives zone↔map navigation and pin/workspace switches across the
  // persistent shell. The state drives the selected pin's
  // overlays (MapWorkspaceLayers) and the filter UI (MapLeftControls).
  const { state: filterState, handlers: filterHandlers } = useLayerVisibilityFilters();

  // Only the station slice, so toggling a traffic band does not re-run the
  // shell's competitor-tiles filter effect.
  const stationFilters = useMemo(
    () => ({
      station: filterState.station,
      isElectraVisible: filterState.isElectraVisible,
      isLowHpcVisible: filterState.isLowHpcVisible,
    }),
    [filterState.station, filterState.isElectraVisible, filterState.isLowHpcVisible],
  );

  // Which zone the camera has been asked to move to. Only two things ask: a zone-index
  // row click (carried in router state, not the URL, so a deep link or a restored `?zone=`
  // never moves the camera) and a search-bar pick (set below). Everything else that lands
  // on `?zone=` — a map click, a deep link, the persisted restore — leaves it null.
  const [focusZoneId, setFocusZoneId] = useState<string | null>(() =>
    (locationState as { focusZone?: boolean } | null)?.focusZone === true ? zoneId : null,
  );
  const consumeZoneFocus = useCallback(() => setFocusZoneId(null), []);

  // Single-zone fetch, inert until `?zone=` is present (a deep link can land
  // before any overlay tile is fetched).
  const { data: fetchedZone, isError, error } = useZoneByIdQuery(zoneId ?? '');

  // The query holds the previous zone while the next one loads, so switching zones swaps the
  // card's contents rather than closing and reopening it. Two cases must not read that held-over
  // value, because it is not the zone `?zone=` names: no `?zone=` at all, which would keep the
  // card open and its cluster hidden, and a failure, which has nothing to show.
  const zone = !hasZone || isError ? undefined : fetchedZone;

  // 404 is expected and user-actionable — NOT a degraded error. Any non-404
  // failure (5xx, transport) degrades gracefully. Both surface as a toast: the
  // card renders on success only, so there is nothing for a failure to occupy.
  //
  // Classify on the status `HttpService` carries, NOT `axios.isAxiosError`:
  // the request layer rewraps every AxiosError into a plain Error, so that
  // predicate is always false in production (see `getErrorStatus`).
  const isNotFound = hasZone && isError && getErrorStatus(error) === 404;
  const isDegradedError = hasZone && isError && !isNotFound;

  // Guard per zone id so the toast fires once per failed zone, not on every
  // re-render. Mirrors the stale-workspace toast above.
  const toastedZoneIdRef = useRef<string | null>(null);

  useEffect(() => {
    // Reset on anything that is not this zone still failing — a cleared param
    // or a different/successful zone. Without this the guard would stay armed
    // on a stale id and silently swallow the toast when the user comes back to
    // a zone that is still broken.
    if (!zoneId || (!isNotFound && !isDegradedError)) {
      toastedZoneIdRef.current = null;
      return;
    }
    if (toastedZoneIdRef.current === zoneId) return;

    toastedZoneIdRef.current = zoneId;

    if (isNotFound) {
      // Definitive: there is nothing to retry, and leaving the id in the URL
      // would also leave it in localStorage (usePersistedSearchParam), so every
      // later cold `/map` visit would reopen a dead zone. `clearZone` drops both.
      toast.error(ZONE_NOT_FOUND_TOAST);
      clearZone();
      return;
    }
    // Transient: keep `?zone=` so a reload retries. Escape still exits.
    toast.error(ZONE_UNREACHABLE_TOAST);
  }, [zoneId, isNotFound, isDegradedError, clearZone]);

  // Whether a zone click should be suppressed because the add-pin tool is
  // claiming this click instead — read synchronously by useZoneInteractions'
  // click handler (see MapShell's suppressSelectionRef / that hook's
  // param doc for why a ref, not this armedToolId value, is what's threaded
  // through: handlePinClick disarms synchronously within the same click, and
  // by the time a deferred effect could read armedToolId here it would
  // already see the post-disarm value).
  const armedForPinRef = useRef(false);
  armedForPinRef.current = armedToolId === ADD_PIN_TOOL_ID;

  /** Navigate to a (neighbour) zone without rendering a popup; re-clicking the selected zone deselects it. */
  function handleZoneSelected(selected: SelectedZone) {
    if (selected.properties.id === zoneId) {
      clearZone();
      return;
    }
    selectZone(selected.properties.id);
  }

  /**
   * A search-bar pick zooms to the zone, like a zone-index row click — the BD
   * asked for that zone by name and has no idea where it is on the map.
   */
  function handleZoneSearchPick(id: string) {
    selectZone(id);
    setFocusZoneId(id);
  }

  /**
   * A deal carries the zone's name, not its id. `GET /zones?name=` is an exact match and returns
   * the id; names are not unique, so a duplicate resolves to the first row, same as search. A
   * result that lands after the deal card has changed is dropped: it would replace whatever the
   * user opened meanwhile.
   */
  const dealIdRef = useRef(dealId);
  useEffect(() => {
    dealIdRef.current = dealId;
  }, [dealId]);
  function openZoneFromDeal(name: string) {
    const fromDealId = dealId;
    void queryClient
      .fetchQuery(zonesKeyFactory.list({ names: [name], limit: 1 }))
      .then((page) => {
        if (dealIdRef.current !== fromDealId) return;
        const zone = page.zones.at(0);
        if (zone == null) {
          toast.error(ZONE_NOT_FOUND_TOAST);
          return;
        }
        handleZoneSearchPick(zone.id);
      })
      .catch((error: unknown) => {
        if (isUnexpectedZonesError(error)) {
          notifyError(error, { context: 'zones:deal-card:resolveName' });
        }
        toast.error(ZONE_UNREACHABLE_TOAST);
      });
  }

  /** A search-bar pick opens the card; the bar itself moves the camera. */
  function handleDealSearchPick(id: string) {
    setDealOpenSource('search');
    selectDeal(id);
  }

  // Escape closes the zone. MapShell's own Escape only clears the click-selection
  // state, which is inert for a zone fetched via useZoneByIdQuery, so the surface
  // handles it here rather than in the card: on a transient failure there is no
  // card, the toast auto-dismisses, and this is the only way out.
  useEffect(() => {
    if (!hasZone) return;
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') clearZone();
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [hasZone, clearZone]);

  return (
    <>
      <NavbarWorkspaceSwitcher
        activeId={activeId}
        onNew={() => setFormState({ mode: 'create' })}
        onEdit={(ws) => setFormState({ mode: 'edit', workspace: ws })}
        onDelete={setDeleteTarget}
        onSelect={setActive}
      />

      <MapShell
        mapId={MAP_SURFACE_ID}
        selectedDealId={dealId}
        onDealSelect={handleDealSelect}
        cornerCardOpen={dealId != null || locationId != null || hasZone}
        onPlaceOpen={() => {
          if (dealId) clearDeal();
          if (locationId) clearLocation();
          if (hasZone) clearZone();
        }}
        // The place card only arms drop pin. The click that follows picks the
        // point, so the place's own coordinates are not the location. The place
        // stays on the row, and a dotted line follows the pointer until the click.
        onCreateLocation={
          activeId != null && !isComputing
            ? (place) => {
                setDropPlace(place);
                arm(ADD_PIN_TOOL_ID);
              }
            : undefined
        }
        defaultCenter={MAP_DEFAULT_CENTER}
        defaultZoom={MAP_DEFAULT_ZOOM}
        // The camera is driven via the map ref — no fake locations.
        fitToLocations={[]}
        // Neighbours overlay follows the control; the selected zone stays outlined
        // regardless, and the overlay stays on in error branches so the BD can keep
        // the map.
        zonesOverlayVisible={zonesOverlayVisible}
        // Narrows both the zones overlay and the deals.
        ownerSelection={ownerSelection}
        visibleZoneTypes={visibleZoneTypes}
        // The map-level tiles layer lives in the shell, so the AADT range
        // checkboxes have to reach it there — MapWorkspaceLayers only filters
        // the per-location layers, and only once a pin is selected.
        visibleTrafficRanges={filterState.traffic}
        // Same reasoning for the map-level competitor stations.
        visibleStationFilters={stationFilters}
        clustersVisible={filterState.isClustersVisible}
        visiblePlaces={filterState.places}
        // No popup on the map: the zone card is the single source of detail.
        renderZoneSelection={() => null}
        // Drops the wide-zoom zone cluster that would otherwise sit over the selected zone.
        // Deals stay on the map; the zone card is what lists this zone's own deals.
        selectedZoneName={zone?.name}
        // The corner deal card; closing it clears `?deal=` via the shell's seam.
        renderDealSelection={({
          dealId: selectedId,
          clearSelection,
          map,
          isMapLoaded: dealMapLoaded,
          imagesInitialized: dealImagesReady,
          dialogContainer,
        }) => (
          <DealMapCard
            key={selectedId}
            map={map}
            isMapLoaded={dealMapLoaded}
            imagesInitialized={dealImagesReady}
            dealId={selectedId}
            onClose={clearSelection}
            onOpenZone={openZoneFromDeal}
            competitorTilesEnabled={mapTilesEnabled}
            stationFilters={stationFilters}
            justPromoted={selectedId === justCreatedDealId}
            openSource={dealOpenSource}
            onPromotionCelebrated={() => setJustCreatedDealId(null)}
            dialogContainer={dialogContainer}
          />
        )}
        // Clicking a zone opens its card.
        onZoneSelected={handleZoneSelected}
        // Clicking the map off every zone closes the card, like clicking off a deal closes its
        // card. `?zone=` is the surface's own state, so the shell's popup clear cannot reach it.
        onZoneDeselected={clearZone}
        suppressSelectionRef={armedForPinRef}
        navControlPosition="bottom-right"
      >
        {(shell) => (
          <>
            {/* Mirrored HubSpot deals: paints the fixed pipeline's stage icons
              and lifts the toggles up to the Deals pill. */}
            <DealsStageSync
              shell={shell}
              ready={mapTilesEnabled && shell.isMapLoaded && shell.imagesInitialized}
              onChange={setDealsPanel}
            />

            {/* Create/edit workspace, driven from the switcher. On create the
              new workspace becomes active via the ?workspace= URL param.
              Viewport-fixed (not portalled into map chrome) so it dims the
              navbar and the map tools. */}
            <WorkspaceFormModal
              open={formState !== null}
              onOpenChange={(next) => {
                if (!next) setFormState(null);
              }}
              mode={formState?.mode ?? 'create'}
              workspace={formState?.mode === 'edit' ? formState.workspace : undefined}
              onCreated={(ws) => setActive(ws.id)}
            />

            {/* Delete confirmation, driven from the switcher kebab. Deleting the
              ACTIVE workspace clears ?workspace=; others leave it untouched.
              Same stacking as create/edit. */}
            <ConfirmDeleteModal
              open={deleteTarget !== null}
              onOpenChange={(next) => {
                if (!next) setDeleteTarget(null);
              }}
              overlayClassName="z-[70]"
              contentClassName="z-[80]"
              isDeleting={deleteWorkspace.isPending}
              title="Delete workspace"
              heading={`Confirm you want to delete "${deleteTarget?.name ?? ''}"`}
              description={`Deleting this workspace removes its ${locationLabel(
                deleteTarget?.location_count ?? 0,
              )}.`}
              onConfirm={() => {
                const target = deleteTarget;
                if (!target) return;
                deleteWorkspace.mutate(target.id, {
                  onSuccess: () => {
                    if (target.id === activeId) clearActive();
                    setDeleteTarget(null);
                  },
                });
              }}
            />

            {/* First-run: no workspace exists yet. The card replaces the search bar and toolbox
              until the first one is created. */}
            {isOnboarding && (
              <MapWorkspaceOnboarding onCreateWorkspace={() => setFormState({ mode: 'create' })} />
            )}

            {/* Active workspace, zero locations: same slot as onboarding
              (mutually exclusive — this requires activeId, onboarding requires
              no workspace at all), but leaves the search bar and toolbox up.
              Both hints step aside when a card holds the slot. */}
            {isWorkspaceEmpty && <MapWorkspaceEmptyLocations besideCard={slotCardOpen(shell)} />}

            {/* Floating hint under the search column: guides the user to the
              switcher when workspaces exist but none is active. */}
            {needsWorkspaceSelection && <MapWorkspaceSelectHint besideCard={slotCardOpen(shell)} />}

            {/* Search, owner and layer pills on one row, the tools under the search.
              Search computes on result when a workspace is active, navigates
              otherwise; blocked while computing or while no workspace is selected.
              The tools are registry-driven (New location, Driving distance).
              Hidden during onboarding. */}
            {!isOnboarding && (
              <MapTopBar
                renderSearch={({ onResultsOpenChange }) => (
                  <MapSearchBar
                    shell={shell}
                    onWorkspaceResult={activeId != null ? handleSearchResult : undefined}
                    onZoneSelect={handleZoneSearchPick}
                    // The mirror is only polled behind map_tiles_enabled, so without it the
                    // section would never have a row to show.
                    onDealSelect={mapTilesEnabled ? handleDealSearchPick : undefined}
                    disabled={isComputing || needsWorkspaceSelection}
                    isComputing={isComputing}
                    onResultsOpenChange={onResultsOpenChange}
                  />
                )}
                renderTools={({ compact }) => (
                  <MapToolbox
                    shell={shell}
                    activeWorkspaceId={activeId ?? null}
                    isComputing={isComputing}
                    armedToolId={armedToolId}
                    onArmChange={handleArmChange}
                    disabled={needsWorkspaceSelection}
                    compact={compact}
                  />
                )}
                // Both surfaces it narrows sit behind map_tiles_enabled, so with the flag off
                // the picker would be inert — and would still fetch the catalogue.
                owner={
                  mapTilesEnabled ? (
                    <MapOwnerFilter
                      selection={ownerSelection}
                      onToggle={toggleOwner}
                      onClear={clearOwners}
                    />
                  ) : undefined
                }
                filters={
                  <MapLayerFilters
                    filterState={filterState}
                    filterHandlers={filterHandlers}
                    dealStages={dealsPanel.stages}
                    onToggleDealStage={dealsPanel.toggleStage}
                    onToggleAllDealStages={dealsPanel.toggleAllStages}
                    zonesVisible={zonesOverlayVisible}
                    onToggleZones={toggleZonesOverlay}
                    visibleZoneTypes={visibleZoneTypes}
                    onToggleZoneType={toggleZoneType}
                  />
                }
              />
            )}

            {/* Manual-pin: a click launches compute directly while add-pin is armed. */}
            <MapPinClickLayer
              shell={shell}
              armed={armedToolId === ADD_PIN_TOOL_ID}
              onLaunch={handlePinClick}
            />
            <PlaceCursorLink
              shell={shell}
              place={armedToolId === ADD_PIN_TOOL_ID ? dropPlace : null}
            />

            {/* Manual-pin: one SSE follower per in-flight compute run.
              Each lives in its own component so concurrent runs each get
              an independent useWorkspaceCompute subscription. On COMPLETED the
              run is cleared and (single-pin) the reconciled marker is auto-selected. */}
            {activeComputeRuns.map((run) => (
              <MapComputeRunFollower
                key={run.projectId}
                run={run}
                onSelectMarker={setAutoSelectId}
                onProgress={setComputeProgress}
                onSettled={(projectId) => {
                  clearComputeRun(projectId);
                  setComputeProgress(null);
                }}
                currentLocationIdRef={selectedLocationIdRef}
              />
            ))}

            {/* Manual-pin: live progress ring on the computing pin, scoped to
              the active workspace's own run so switching workspace mid-compute
              doesn't leave another workspace's pin showing on this map.
              Clicking it re-selects the run's tempId so the skeleton (with
              its Cancel button) reopens after the user has looked at another
              location while this one keeps computing. */}
            {activeWorkspaceComputeRun?.coordinates != null && (
              <MapComputePinMarker
                shell={shell}
                coordinates={activeWorkspaceComputeRun.coordinates}
                progress={computeProgress}
                onSelect={() => selectLocation(activeWorkspaceComputeRun.tempIds[0])}
              />
            )}

            {/* A `?location=` link written on a HubSpot deal points at the location the deal
              was created from, which left the marker set the moment it was promoted. Resolve
              it to the deal's own card, and fly there: arriving from the portal, the pin's
              whereabouts are the one thing the card cannot tell. */}
            <PromotedLocationRedirect
              locationId={locationId}
              deals={shell.deals}
              onSelectDeal={selectDeal}
              map={shell.map}
              isMapLoaded={shell.isMapLoaded}
            />

            {/* Workspace markers: when a workspace is active, render all its
              locations as clustered pins over the persistent shell. The camera fit
              is skipped, not deferred, while a zone or deal card is open (`allowFit`). */}
            {activeId != null && (
              <MapWorkspaceLayers
                shell={shell}
                workspaceId={activeId}
                allowFit={!hasZone && dealId == null}
                autoSelectId={autoSelectId}
                // Layer-visibility filters: applied to the selected pin's
                // overlays via an effect inside MapWorkspaceLayers.
                filterState={filterState}
                competitorTilesEnabled={mapTilesEnabled}
                locationId={locationId}
                onSelectLocation={selectLocation}
                onClearLocation={clearLocation}
                onSelectDeal={selectDeal}
                onDealCreated={handleDealCreated}
                computingLocationIds={computingLocationIds}
                computingProgress={computeProgress}
                computingProjectId={computingRun?.projectId ?? null}
                onCancelComputing={handleCancelComputing}
              />
            )}

            {/* Outline the selected zone once data + map are ready, and fit to it
              when the surface was entered with a focus intent. Error branches have
              no geometry, so it's gated on `zone`. */}
            {hasZone && zone != null && (
              <MapZoneFocus
                shell={shell}
                zone={zone}
                focusZoneId={focusZoneId}
                onFocusConsumed={consumeZoneFocus}
              />
            )}

            {/* The selected zone's card, in the same top-left slot as a deal or a place.
              It stays open through zoom, the way those cards do. Reads the deals payload
              the shell already polls, so selecting a zone costs no request. A deal,
              location, or place taking the slot closes the zone. */}
            {hasZone && zone != null && (
              <ZoneMapCard
                zone={zone}
                deals={shell.deals}
                onSelectDeal={selectDeal}
                onClose={clearZone}
                dialogContainer={shell.containerRef.current ?? undefined}
              />
            )}
          </>
        )}
      </MapShell>
    </>
  );
}

function PlaceCursorLink({
  shell,
  place,
}: {
  shell: MapShellRenderState;
  place: PointOfInterestPlace | null;
}) {
  usePlaceCursorLink(shell.map, shell.isMapLoaded ? place : null);
  return null;
}

// ── MapPinClickLayer — map-click → launch compute ───────────────────────────

interface MapPinClickLayerProps {
  shell: MapShellRenderState;
  /** Whether the add-pin tool is armed; clicks only launch compute while true. */
  armed: boolean;
  /** Launches a compute run at the clicked coordinates; zoneId is the hunting
   *  zone under the click, if any (Mixpanel attribution only — no navigation). */
  onLaunch: (coords: { latitude: number; longitude: number }, zoneId: string | null) => void;
}

/**
 * Side-effect-only child that, while the add-pin tool is armed, turns a map
 * click into an immediate compute-run launch at the clicked `lngLat`. Also
 * sets the map canvas cursor to `crosshair` while armed and restores it on
 * disarm / unmount. Renders `null`; the listener is torn down on disarm or
 * unmount so a stray click never launches anything once the tool is put away.
 */
function MapPinClickLayer({ shell, armed, onLaunch }: MapPinClickLayerProps) {
  const { map, isMapLoaded } = shell;

  useEffect(() => {
    if (!armed) return;
    const currentMap = map.current;
    if (!currentMap) return;

    function handleClick(event: { lngLat: { lng: number; lat: number } }): void {
      const zoneId = map.current ? zoneIdAtLngLat(map.current, event.lngLat) : null;
      onLaunch({ latitude: event.lngLat.lat, longitude: event.lngLat.lng }, zoneId);
    }

    currentMap.on('click', handleClick);

    // Set crosshair cursor while armed; capture the previous value so we
    // restore it exactly on disarm / unmount.
    const canvas = currentMap.getCanvas();
    const prevCursor = canvas.style.cursor;
    canvas.style.cursor = 'crosshair';

    return () => {
      currentMap.off('click', handleClick);
      canvas.style.cursor = prevCursor;
    };
    // isMapLoaded is a dep so the listener re-binds onto a fresh map instance if
    // the shell ever reloads its map.
  }, [armed, map, isMapLoaded, onLaunch]);

  return null;
}

// ── MapComputePinMarker — live progress ring on the computing pin ───────────

interface MapComputePinMarkerProps {
  shell: MapShellRenderState;
  coordinates: Coordinates;
  /** 0–100, or null before the first SSE progress message arrives. */
  progress: number | null;
  /** Re-selects the run's location on click, reopening its skeleton popup
   *  (with the Cancel button) after the user has selected another pin. */
  onSelect: () => void;
}

/**
 * DOM marker at the launch coordinates showing the compute run's live
 * progress as a circular ring + percentage, mirroring the design mock's pin.
 * Torn down on unmount (the run settling removes this component from the tree).
 */
function MapComputePinMarker({ shell, coordinates, progress, onSelect }: MapComputePinMarkerProps) {
  const { map, isMapLoaded } = shell;
  const ringRef = useRef<HTMLDivElement | null>(null);
  const pctRef = useRef<HTMLSpanElement | null>(null);
  // Latest-value ref so the DOM click listener (attached once, in the
  // mount effect below) always calls the current `onSelect` without
  // re-creating the marker element on every render.
  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;

  useEffect(() => {
    if (!isMapLoaded || !map.current) return;

    const el = document.createElement('div');
    el.className =
      'relative flex size-[46px] cursor-pointer items-center justify-center rounded-full drop-shadow-md';
    el.addEventListener('click', () => onSelectRef.current());

    const ring = document.createElement('div');
    ring.className = 'absolute inset-0 rounded-full';
    el.appendChild(ring);
    ringRef.current = ring;

    const disc = document.createElement('div');
    disc.className = 'absolute inset-[5px] rounded-full border-2 border-white bg-primary-700';
    el.appendChild(disc);

    const pct = document.createElement('span');
    pct.className = 'relative z-10 text-[11px] font-bold text-white';
    el.appendChild(pct);
    pctRef.current = pct;

    const marker = new mapboxgl.Marker({ element: el, anchor: 'center' })
      .setLngLat([coordinates.longitude, coordinates.latitude])
      .addTo(map.current);

    return () => {
      marker.remove();
      ringRef.current = null;
      pctRef.current = null;
    };
  }, [map, isMapLoaded, coordinates.latitude, coordinates.longitude]);

  useEffect(() => {
    const pct = Math.round(progress ?? 0);
    if (ringRef.current) {
      ringRef.current.style.background = `conic-gradient(var(--brand) ${pct}%, var(--border) 0)`;
    }
    if (pctRef.current) {
      pctRef.current.textContent = `${pct}%`;
    }
  }, [progress]);

  return null;
}

// ── MapComputeRunFollower — one SSE follower per compute run ─────────────────

interface MapComputeRunFollowerProps {
  run: ComputeRun;
  /** Auto-select setter raised on a single-pin COMPLETED. */
  onSelectMarker: (markerId: string) => void;
  /** Raised with the 0–100 percent on every SSE progress message. */
  onProgress: (percent: number) => void;
  /** Clears the run from context once its SSE terminal has been handled. */
  onSettled: (projectId: string) => void;
  /** Live pointer to the currently-selected location; see useWorkspaceCompute's ctx doc. */
  currentLocationIdRef: React.RefObject<string | null>;
}

/**
 * Follows a single compute run over SSE via {@link useWorkspaceCompute}. One
 * instance per run keeps each subscription independent so concurrent batches do
 * not interfere. `onSelectMarker` opens the single reconciled marker on
 * a single-pin COMPLETED (unless the user has since selected something else);
 * `onSettled` drops the run from context on EITHER terminal so the computing
 * indicator clears on both outcomes.
 */
function MapComputeRunFollower({
  run,
  onSelectMarker,
  onProgress,
  onSettled,
  currentLocationIdRef,
}: MapComputeRunFollowerProps) {
  useWorkspaceCompute(run.projectId, {
    workspaceId: run.workspaceId,
    tempIds: run.tempIds,
    autoSelectSingle: run.autoSelectSingle,
    coordinates: run.coordinates,
    existingMarkerIds: run.existingMarkerIds,
    onSelectMarker,
    onProgress,
    onSettled: () => onSettled(run.projectId),
    currentLocationIdRef,
  });

  return null;
}
